/**
 * @class TabsList
 * @extends HTMLElement
 *
 * Self-contained desktop tab list.
 *
 * Expects:
 *   - Tabs:   [role="tab"] buttons with aria-controls pointing to a panel ID
 *   - Panels: [role="tabpanel"] elements
 */
class TabsList extends HTMLElement {
  #tabs = [];
  #panels = [];

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('keydown', this.#onTabKeyDown);
    });
  }

  disconnectedCallback() {
    this.#tabs.forEach((tab) => {
      tab.removeEventListener('click', this.#onTabClick);
      tab.removeEventListener('keydown', this.#onTabKeyDown);
    });

    this.#tabs = [];
    this.#panels = [];
  }

  #onTabClick = (event) => {
    this.#activate(event.currentTarget);
  };

  #onTabKeyDown = (event) => {
    const currentIndex = this.#tabs.indexOf(event.currentTarget);
    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = this.#tabs[nextIndex];
    nextTab.focus();
    this.#activate(nextTab);
  };

  #activate(tab) {
    const panelId = tab.getAttribute('aria-controls');

    this.#tabs.forEach((item) => {
      const isActive = item === tab;
      item.setAttribute('aria-selected', String(isActive));
      item.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
  }
}

if (!customElements.get('tabs-list')) {
  customElements.define('tabs-list', TabsList);
}
